import { PlayerHeader } from "@/components/general/Player/PlayerHeader";
import { PlayerMetrics } from "@/components/general/Player/PlayerMetrics";
import { useParams, useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { playerDataQueryOptions, latestPlayerDataQueryOptions } from "@/api/player";
import { useSelectedTeam } from "@/context/teamContext";
import { playerTournamentsQueryOptions } from "@/api/tournaments";
import { useState, useEffect, useRef } from "react";
import { RouteUrl } from "@/configs/router/routes";
import { PlayerAchievements } from "@/components/general/Player/PlayerAchievements/PlayerAchievements";
import { PageNotFound } from "@/pages/notFound/page";
import PlayerPizzaChart from "@/components/general/Player/PlayerPizzaChart";
import type { SkillCategory } from "@/components/general/Player/PlayerPizzaChart";
import { useSelectedTournament } from "@/context/tournamentContext";
import { PlayerKPIChart } from "@/components/general/Player/PlayerCharts/PlayerKPIChart";
import { PlayerKPIDetailsChart } from "@/components/general/Player/PlayerCharts/PlayerKPIDetailsChart";
import { PlayerGoalkeeperKPIDetailsChart } from "@/components/general/Player/PlayerCharts/PlayerGoalkeeperKPIDetailsChart";
import { PlayerFeedbackPanel } from "@/components/general/Player/PlayerFeedbackPanel";
import { PlayerRinkPositionMap } from "@/components/general/Player/PlayerRinkPositionMap";
import { PlayerBanner } from "@/components/general/Player/PlayerBanner";

const PlayerProfile = () => {
    const { playerId: playerIdParam } = useParams({ from: "/_pathlessLayout/player/$playerId" });
    const { teamId: searchTeamId } = useSearch({ from: "/_pathlessLayout/player/$playerId" });
    const playerId = parseInt(playerIdParam);
    const { tournamentId: globalTournamentId, setTournamentId: setGlobalTournamentId } = useSelectedTournament();
    const { teamId: contextTeamId } = useSelectedTeam();
    const teamId = searchTeamId ?? contextTeamId;
    const navigate = useNavigate();
    const prevTeamId = useRef(contextTeamId);

    useEffect(() => {
        if (prevTeamId.current !== 0 && contextTeamId !== 0 && contextTeamId !== prevTeamId.current) {
            navigate({ to: RouteUrl.Players });
        }
        prevTeamId.current = contextTeamId;
    }, [contextTeamId, navigate]);
    const { data: playerTournaments, isLoading: tournamentsLoading } = useQuery(
        playerTournamentsQueryOptions(playerId)
    );
    const [localTournamentId, setLocalTournamentId] = useState<number | undefined>(undefined);
    const [playerName, setPlayerName] = useState<string | undefined>(undefined);
    const [selectedKPI, setSelectedKPI] = useState("totalPoints");
    const [kpiTimelineTitle, setKpiTimelineTitle] = useState<{ main: string; sub: string; subColor?: string } | null>(
        null
    );
    const [kpiBreakdownTitle, setKpiBreakdownTitle] = useState<{ main: string; sub: string; subColor?: string } | null>(
        null
    );

    useEffect(() => {
        if (globalTournamentId && localTournamentId === undefined && playerTournaments) {
            const globalTournament = playerTournaments.find((t) => t.id === globalTournamentId);
            if (globalTournament?.doesPlayerParticipate) {
                setLocalTournamentId(globalTournamentId);
            } else {
                const latestParticipatedTournament = playerTournaments
                    .filter((t) => t.doesPlayerParticipate)
                    .sort((a, b) => b.id - a.id)[0];
                if (latestParticipatedTournament) {
                    setLocalTournamentId(latestParticipatedTournament.id);
                }
            }
        }
    }, [globalTournamentId, localTournamentId, playerTournaments]);

    const playerHasGlobalTournament = playerTournaments?.find(
        (t) => t.id === globalTournamentId
    )?.doesPlayerParticipate;
    const selectedTournamentId =
        localTournamentId !== undefined
            ? localTournamentId
            : playerHasGlobalTournament
              ? globalTournamentId
              : undefined;
    const shouldFetchLatest = selectedTournamentId === undefined;

    const {
        data: latestPlayerData,
        isLoading: latestPlayerDataLoading,
        error: latestPlayerError,
    } = useQuery({
        ...latestPlayerDataQueryOptions(playerIdParam, teamId),
        enabled: !!playerId && shouldFetchLatest,
    });

    const isSelectedTournamentSameAsLatest = latestPlayerData?.tournamentId === selectedTournamentId;

    const {
        data: tournamentPlayerData,
        isLoading: tournamentPlayerDataLoading,
        error: tournamentPlayerError,
    } = useQuery({
        ...playerDataQueryOptions(playerIdParam, selectedTournamentId || 0, teamId),
        enabled:
            !!playerId &&
            !!selectedTournamentId &&
            selectedTournamentId > 0 &&
            !shouldFetchLatest &&
            !isSelectedTournamentSameAsLatest,
    });

    const currentTournamentId = selectedTournamentId || (latestPlayerData?.tournamentId ?? undefined);
    const currentTournament = playerTournaments?.find((t) => t.id === currentTournamentId);
    const selectedTournamentName = currentTournament?.name;

    const playerData = shouldFetchLatest || isSelectedTournamentSameAsLatest ? latestPlayerData : tournamentPlayerData;
    const isLoading =
        tournamentsLoading ||
        (shouldFetchLatest || isSelectedTournamentSameAsLatest ? latestPlayerDataLoading : tournamentPlayerDataLoading);
    const playerError =
        shouldFetchLatest || isSelectedTournamentSameAsLatest ? latestPlayerError : tournamentPlayerError;

    useEffect(() => {
        if (latestPlayerData?.tournamentId && selectedTournamentId === undefined && localTournamentId === undefined) {
            setLocalTournamentId(latestPlayerData.tournamentId);
        }
    }, [latestPlayerData, selectedTournamentId, localTournamentId]);

    useEffect(() => {
        if (playerData && !playerName) {
            setPlayerName(`${playerData.firstName} ${playerData.lastName}`);
        }
    }, [playerData, playerName]);

    const handleTournamentChange = (tournamentName: string) => {
        const tournament = playerTournaments?.find((t) => t.name === tournamentName);
        if (tournament) {
            setLocalTournamentId(tournament.id);
            setGlobalTournamentId(tournament.id);
            setPlayerName(undefined);
        }
    };

    if (playerError && (playerError as Error).message?.includes("404")) {
        return <PageNotFound hideLayout />;
    }

    const isGoalkeeper = playerData?.role?.includes("GK") ?? false;
    const overallKpiEntry = playerData?.kpiPerRole?.find((r) => r.role === "Overall");
    const avgKpi =
        overallKpiEntry?.avgKpi ??
        (playerData?.kpiPerRole && playerData.kpiPerRole.length > 0
            ? playerData.kpiPerRole.reduce((s, r) => s + (r.avgKpi ?? 0), 0) / playerData.kpiPerRole.length
            : 0);

    const skillCategories: SkillCategory[] = (() => {
        if (!playerData) return [];
        if (isGoalkeeper && (playerData.goalkeeperKpis?.length ?? 0) > 0) {
            const gkKpis = playerData.goalkeeperKpis!;
            const n = gkKpis.length;
            const p = playerData.kpiPercentileRanksGoalkeeper;
            return [
                {
                    label: "GSAx Points",
                    percentile: p?.gsAx ?? 0,
                    value: gkKpis.reduce((s, k) => s + (k.pointsGSAx.pointsGSAx ?? 0), 0) / n,
                    max: playerData.kpiLeagueAvgsGoalkeeper?.pointsGSAxAvg ?? 30,
                    color: "var(--color-general-orange)",
                },
                {
                    label: "High Danger",
                    percentile: p?.highDanger ?? 0,
                    value: gkKpis.reduce((s, k) => s + (k.zone1.pointsHighDangerShots ?? 0), 0) / n,
                    max: playerData.kpiLeagueAvgsGoalkeeper?.pointsHighDangerShotsAvg ?? 20,
                    color: "var(--color-general-red)",
                },
                {
                    label: "Low Danger",
                    percentile: p?.lowDanger ?? 0,
                    value: gkKpis.reduce((s, k) => s + (k.zone4.pointsLowDangerShots ?? 0), 0) / n,
                    max: playerData.kpiLeagueAvgsGoalkeeper?.pointsLowDangerShotsAvg ?? 30,
                    color: "var(--color-general-green)",
                },
                {
                    label: "Lateral Pass",
                    percentile: p?.lateralPass ?? 0,
                    value: gkKpis.reduce((s, k) => s + (k.lateralPass.pointsLateralPassShots ?? 0), 0) / n,
                    max: playerData.kpiLeagueAvgsGoalkeeper?.pointsLateralPassShotsAvg ?? 10,
                    color: "var(--color-general-yellow)",
                },
                {
                    label: "Screened Shots",
                    percentile: p?.screened ?? 0,
                    value: gkKpis.reduce((s, k) => s + (k.screened.pointsScreenedShots ?? 0), 0) / n,
                    max: playerData.kpiLeagueAvgsGoalkeeper?.pointsScreenedShotsAvg ?? 10,
                    color: "var(--color-general-purple)",
                },
            ];
        }
        const fieldKpis = playerData.kpis ?? [];
        if (fieldKpis.length === 0) return [];
        const n = fieldKpis.length;
        const p = playerData.kpiPercentileRanks;
        return [
            {
                label: "Offensive",
                percentile: p?.offensive ?? 0,
                value: fieldKpis.reduce((s, k) => s + (k.offensiveData.points ?? 0), 0) / n,
                max: playerData.kpiLeagueAvgs?.offensivePointsAvg ?? 24,
                color: "var(--color-general-red)",
            },
            {
                label: "Defensive",
                percentile: p?.defensive ?? 0,
                value: fieldKpis.reduce((s, k) => s + (k.defensiveData.points ?? 0), 0) / n,
                max: playerData.kpiLeagueAvgs?.defensivePointsAvg ?? 36,
                color: "var(--color-general-green)",
            },
            {
                label: "Team Play",
                percentile: p?.teamPlay ?? 0,
                value: fieldKpis.reduce((s, k) => s + (k.teamPlayData.points ?? 0), 0) / n,
                max: playerData.kpiLeagueAvgs?.teamPlayPointsAvg ?? 30,
                color: "var(--color-general-yellow)",
            },
            {
                label: "Effort",
                percentile: p?.effort ?? 0,
                value: fieldKpis.reduce((s, k) => s + (k.effortData.points ?? 0), 0) / n,
                max: playerData.kpiLeagueAvgs?.effortPointsAvg ?? 10,
                color: "var(--color-general-purple)",
            },
            {
                label: "Bonus",
                percentile: p?.bonus ?? 0,
                value: fieldKpis.reduce((s, k) => s + (k.bonusData.points ?? 0), 0) / n,
                max: playerData.kpiLeagueAvgs?.bonusPointsAvg ?? 10,
                color: "var(--color-general-orange)",
            },
        ];
    })();

    const matchCount = isGoalkeeper ? (playerData?.goalkeeperKpis?.length ?? 0) : (playerData?.kpis?.length ?? 0);
    const chartsInRow = !isLoading && matchCount > 0 && matchCount <= 10;

    return (
        <main data-player-loaded={!isLoading ? "true" : undefined} className="p-4 md:p-6 flex flex-col gap-6">
            <PlayerHeader
                playerName={playerName}
                playerTeamId={teamId}
                selectedTournament={selectedTournamentName}
                onTournamentChange={handleTournamentChange}
                playerId={playerId}
                selectedTournamentId={currentTournamentId}
                playerTournaments={playerTournaments}
            />

            <div id="onboarding-player-hero" className="flex flex-col gap-4">
                <PlayerBanner playerId={playerId} playerData={playerData} isLoading={isLoading} />
            </div>

            <div id="onboarding-player-skills" className="grid grid-cols-1 lg:grid-cols-3 gap-4 -mt-3 md:mt-0">
                <PlayerMetrics playerData={playerData} isLoading={isLoading} />

                <div className="flex flex-col gap-1">
                    <h3 className="px-4 pt-3 pb-1 text-sm font-headline font-bold section-title">Skill Radar</h3>
                    <div className="flex-1 min-h-[220px] rounded-xl overflow-hidden" style={{ background: "#050066" }}>
                        <PlayerPizzaChart
                            playerData={playerData}
                            isLoading={isLoading}
                            darkMode
                            avgKpi={!isLoading ? avgKpi : undefined}
                            skillCategories={skillCategories}
                        />
                    </div>
                </div>

                {!isGoalkeeper && (
                    <div id="onboarding-player-rink-map" className="flex flex-col gap-1">
                        <h3 className="px-4 pt-3 pb-1 text-sm font-headline font-bold section-title">
                            Position Performance
                        </h3>
                        <div className="flex-1 min-h-0">
                            <PlayerRinkPositionMap playerData={playerData} isLoading={isLoading} />
                        </div>
                    </div>
                )}

                {currentTournamentId && (
                    <div className={`flex flex-col gap-1 ${isGoalkeeper ? "lg:col-span-2" : ""}`}>
                        <PlayerFeedbackPanel playerId={playerId} tournamentId={currentTournamentId} teamId={teamId} />
                    </div>
                )}
            </div>

            <div className={chartsInRow ? "flex flex-wrap gap-4 items-stretch" : "flex flex-col gap-4"}>
                <div
                    id="onboarding-player-kpi-chart"
                    className={`flex flex-col gap-1${chartsInRow ? " basis-[500px] grow" : ""}`}
                >
                    <h3 className="px-4 pt-3 pb-1 text-sm font-headline font-bold section-title">
                        {kpiTimelineTitle ? (
                            <>
                                {kpiTimelineTitle.main}
                                <span className="text-on-surface-variant font-normal"> • </span>
                                <span style={{ color: kpiTimelineTitle.subColor }}>{kpiTimelineTitle.sub}</span>
                            </>
                        ) : (
                            "KPI Timeline"
                        )}
                    </h3>
                    <div className="rounded-xl overflow-hidden bg-surface-container-highest h-[320px] md:h-[360px] lg:h-[400px]">
                        <PlayerKPIChart
                            playerData={playerData}
                            selectedKPI={selectedKPI}
                            onKPIChange={setSelectedKPI}
                            isLoading={isLoading}
                            onTitleChange={setKpiTimelineTitle}
                        />
                    </div>
                </div>
                <div
                    id="onboarding-player-kpi-details"
                    className={`flex flex-col gap-1${chartsInRow ? " basis-[500px] grow" : ""}`}
                >
                    <h3 className="px-4 pt-3 pb-1 text-sm font-headline font-bold section-title">
                        {kpiBreakdownTitle ? (
                            <>
                                {kpiBreakdownTitle.main}
                                <span className="text-on-surface-variant font-normal"> • </span>
                                <span style={{ color: kpiBreakdownTitle.subColor }}>{kpiBreakdownTitle.sub}</span>
                            </>
                        ) : (
                            "KPI Breakdown"
                        )}
                    </h3>
                    <div className="rounded-xl overflow-hidden bg-surface-container-highest h-[320px] md:h-[360px] lg:h-[400px]">
                        {isGoalkeeper ? (
                            <PlayerGoalkeeperKPIDetailsChart
                                playerData={playerData}
                                selectedKPI={selectedKPI}
                                isLoading={isLoading}
                                onTitleChange={setKpiBreakdownTitle}
                            />
                        ) : (
                            <PlayerKPIDetailsChart
                                playerData={playerData}
                                selectedKPI={selectedKPI}
                                isLoading={isLoading}
                                onTitleChange={setKpiBreakdownTitle}
                            />
                        )}
                    </div>
                </div>
            </div>

            <div id="onboarding-player-achievements" className="flex flex-col">
                <PlayerAchievements playerData={playerData} isLoading={isLoading} />
            </div>

            <div className="md:hidden py-6" />
        </main>
    );
};

export default PlayerProfile;
